import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, FormHelperText } from '@mui/material';
import PracticeNotesModal from './PracticeNotesModal';
import '../labPractice/LabPractice.css';
import PracticeNotesTable from './PracticeNotesTable';
import { getAllCOE } from '../../../services/adminServices/CoeServices';
import { getAllCoeLabs } from '../../../services/mentorServices/DailyPracticeServices';
import ConfirmDisableModal from '../../ConfirmDisableModal';
import { disablePracticeNotes, getAllPracticeNotes, getPracticeNotesQR } from '../../../services/mentorServices/PracticeNotesServices';
import { toast } from 'react-toastify';

const PracticeNotes = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selection, setSelection] = useState({ lab: '', coe: '' });
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [error, setError] = useState({ lab: '', coe: '' });
    const [coes, setCoe] = useState([]);
    const [labNames, setLabNames] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [disableModalOpen, setDisableModalOpen] = useState(false);
    const [selectedPractice, setSelectedPractice] = useState(null);

    const handleModalToggle = () => setModalOpen(prev => !prev);

    const handleSelectionChange = (event) => {
        const { name, value } = event.target;
        setSelection(prev => ({ ...prev, [name]: value }));
        if (name === "coe") {
            fetchLabs(value);
            setSelection(prev => ({ ...prev, lab: '', coe: value }));
        }
        setError(prev => ({ ...prev, [name]: '' }));
    };

    const fetchData = useCallback(async () => {
        const { lab, coe } = selection;
        if (!lab || !coe) {
            setError({
                lab: lab ? '' : 'Please select a Lab',
                coe: coe ? '' : 'Please select a COE',
            });
            return;
        }
        setLoading(true);
        try {
            const response = await getAllPracticeNotes(coe, lab);
            setTableData(response.data.data);
            setShowTable(true);
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    }, [selection]);

    const getCoe = async () => {
        try {
            const response = await getAllCOE();
            setCoe(response.data.data);
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    };

    const fetchLabs = useCallback(async (coeId) => {
        try {
            const response = await getAllCoeLabs(coeId);
            setLabNames(response?.data.data || []);
        } catch (error) {
            setLabNames([]);
            setTableData([]);
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    }, []);

    const disablePractice = async () => {
        if (!selectedPractice) return;
        try {
            const response = await disablePracticeNotes(
                selectedPractice.daily_note_id,
                selection.coe,
                selection.lab,
                selectedPractice?.makerplanID,
                selectedPractice?.moduleID,
                selectedPractice?.topicID
            );
            toast.success(response?.data.message);
            fetchData();
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        } finally {
            setDisableModalOpen(false);
            setSelectedPractice(null);
        }
    };

    const downloadQR = async (row) => {
        try {
            const response = await getPracticeNotesQR(row.daily_note_id);
            if (response?.data) {
                const blob = new Blob([response.data], { type: 'image/png' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `PracticeNotes-${row?.makerplan_name?.trim()}-${row?.module_name?.trim()}-${row?.topic_name?.trim()}-${row?.note_name?.trim()}.png`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success(response?.data.message);
            } else {
                throw new Error('Invalid response data');
            }
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    };

    useEffect(() => {
        getCoe();
    }, []);

    return (
        <Box className="dashboard-container">
            <Box className="DailyPractice-header">
                <Box className="daily-practice-inputs" display="flex" sx={{ width: "60% !important" }} alignItems="center">
                    <FormControl size="small" className="lab-name-select" sx={{ width: "43% !important" }} error={!!error.coe}>
                        <InputLabel id="coe-select-label">COE*</InputLabel>
                        <Select
                            labelId="coe-select-label"
                            id="coe-select"
                            name="coe"
                            value={selection.coe}
                            onChange={handleSelectionChange}
                            label="COE"
                        >
                            {coes.map((coe, index) => (
                                <MenuItem key={index} value={coe.id}>
                                    {coe.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.coe && <FormHelperText>{error.coe}</FormHelperText>}
                    </FormControl>

                    <FormControl size="small" className="lab-name-select" sx={{ width: "43% !important" }} error={!!error.lab}>
                        <InputLabel id="lab-select-label">Lab Name*</InputLabel>
                        <Select
                            labelId="lab-select-label"
                            id="lab-select"
                            name="lab"
                            value={selection.lab}
                            onChange={handleSelectionChange}
                            label="Lab Name"
                        >
                            {labNames.map((lab, index) => (
                                <MenuItem key={index} value={lab.id}>
                                    {lab.lab_name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.lab && <FormHelperText>{error.lab}</FormHelperText>}
                    </FormControl>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={fetchData}
                        className="dashboard-header-button"
                        disabled={loading || !selection.lab || !selection.coe || labNames.length === 0}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'VIEW'}
                    </Button>
                </Box>

                {showTable && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleModalToggle}
                        className="dashboard-header-button"
                        disabled={!selection.coe || !selection.lab}
                    >
                        CREATE PRACTICE NOTES
                    </Button>
                )}

                <PracticeNotesModal
                    open={modalOpen}
                    onClose={handleModalToggle}
                    selection={selection}
                    selectedRow={selectedRow}
                    fetchData={fetchData}
                    setSelectedRow={setSelectedRow}
                />
            </Box>

            <Box className="dashboard-box-container">
                {showTable && (
                    <PracticeNotesTable
                        data={tableData}
                        handleModalToggle={handleModalToggle}
                        setSelectedRow={setSelectedRow}
                        disablePractice={disablePractice}
                        downloadQR={downloadQR}
                        selectedPractice={selectedPractice}
                        setSelectedPractice={setSelectedPractice}
                        setDisableModalOpen={setDisableModalOpen}
                        modalOpen={modalOpen}
                        loading={loading}
                        setLoading={setLoading}
                    />
                )}
            </Box>

            <ConfirmDisableModal
                open={disableModalOpen}
                onClose={() => setDisableModalOpen(false)}
                onConfirm={disablePractice}
                message="Practice Notes"
            />
        </Box>
    );
};

export default PracticeNotes;
